import { useEffect, useRef, type RefObject } from 'react';

interface IUseAutoToBottomScrollerProps {
  isStartPointOfNewConversation: boolean;
  autoScrollContainerRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
  isScrollerEnabled: boolean;
}

export const useAutoToBottomScroller = ({
  isStartPointOfNewConversation,
  autoScrollContainerRef,
  containerRef,
  isScrollerEnabled,
}: IUseAutoToBottomScrollerProps) => {
  const isManualScrollingRef = useRef(false);

  useEffect(() => {
    if (isScrollerEnabled && isStartPointOfNewConversation) {
      isManualScrollingRef.current = false;
    }
  }, [isScrollerEnabled, isStartPointOfNewConversation]);

  useEffect(() => {
    if (!isScrollerEnabled) {
      return () => {};
    }

    const containerEl = containerRef.current;
    const autoScrollContainer = autoScrollContainerRef.current;
    let previousScrollHeight = containerEl?.scrollHeight || 0;

    if (!containerEl || !autoScrollContainer) {
      return () => {};
    }

    const handleWheel = () => {
      isManualScrollingRef.current = true;
    };
    containerEl.addEventListener('wheel', handleWheel);

    const observer = new MutationObserver(() => {
      const isScrollbarVisible = containerEl.scrollHeight > containerEl.offsetHeight;
      containerEl.classList.toggle('scrollbar-visible', isScrollbarVisible);
      containerEl.classList.toggle('scrollbar-hidden', !isScrollbarVisible);

      const currentScrollHeight = containerEl.scrollHeight;

      if (currentScrollHeight > previousScrollHeight && !isManualScrollingRef.current) {
        requestAnimationFrame(() => {
          containerEl.scrollTo({
            top: currentScrollHeight,
            behavior: 'smooth',
          });
        });
      }
      previousScrollHeight = currentScrollHeight;
    });

    observer.observe(containerEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    requestAnimationFrame(() => {
      containerEl.scrollTo({
        top: containerEl.scrollHeight,
        behavior: 'auto',
      });
      previousScrollHeight = containerEl.scrollHeight;
    });

    return () => {
      containerEl.removeEventListener('wheel', handleWheel);
      observer.disconnect();
    };
  }, [autoScrollContainerRef, containerRef, isScrollerEnabled]);
};
